import type { Temporal } from '@js-temporal/polyfill'

export type State = {
  inputValue: string
  isOpen: boolean
  activeIndex: number
  isFocused: boolean
  internalValue: Temporal.PlainTime | null
  userNavigated: boolean
  shouldScrollToActive: boolean
}

export type Action =
  | { type: 'SET_INPUT'; value: string; resetNavigation?: boolean }
  | { type: 'OPEN_MENU' }
  | { type: 'CLOSE_MENU' }
  | {
      type: 'SET_ACTIVE_INDEX'
      index: number
      userInitiated?: boolean
      shouldScroll?: boolean
    }
  | { type: 'NAVIGATE'; direction: 'up' | 'down'; length: number }
  | { type: 'SET_FOCUS'; isFocused: boolean }
  | { type: 'SET_INTERNAL_VALUE'; value: Temporal.PlainTime | null }
  | { type: 'SET_USER_NAVIGATED'; userNavigated: boolean }
  | { type: 'AUTO_HIGHLIGHT'; index: number }
  | { type: 'RESET_ACTIVE_INDEX' }

export const initialReducerState: State = {
  inputValue: '',
  isOpen: false,
  activeIndex: -1,
  isFocused: false,
  internalValue: null,
  userNavigated: false,
  shouldScrollToActive: false,
}

export const timeInputReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_INPUT':
      return {
        ...state,
        inputValue: action.value,
        userNavigated: action.resetNavigation ? false : state.userNavigated,
      }
    case 'OPEN_MENU':
      return {
        ...state,
        isOpen: true,
        userNavigated: false,
      }
    case 'CLOSE_MENU':
      return {
        ...state,
        isOpen: false,
        activeIndex: -1,
        userNavigated: false,
      }
    case 'SET_ACTIVE_INDEX':
      return {
        ...state,
        activeIndex: action.index,
        userNavigated: action.userInitiated ?? state.userNavigated,
        shouldScrollToActive: action.shouldScroll ?? true,
      }
    case 'NAVIGATE': {
      if (action.length <= 0) {
        return { ...state, activeIndex: -1 }
      }
      const current = state.activeIndex

      // When no item is selected (current === -1), navigate directly to first/last item
      if (current === -1) {
        const nextIndex = action.direction === 'down' ? 0 : action.length - 1
        return {
          ...state,
          activeIndex: nextIndex,
          userNavigated: true,
          isOpen: true,
          shouldScrollToActive: true,
        }
      }

      // When navigating from an existing selection, move to next/previous item
      const nextIndex =
        action.direction === 'down'
          ? Math.min(current + 1, action.length - 1)
          : Math.max(current - 1, 0)
      return {
        ...state,
        activeIndex: nextIndex,
        userNavigated: true,
        isOpen: true,
        shouldScrollToActive: true,
      }
    }
    case 'SET_FOCUS':
      return {
        ...state,
        isFocused: action.isFocused,
      }
    case 'SET_INTERNAL_VALUE':
      return {
        ...state,
        internalValue: action.value,
      }
    case 'SET_USER_NAVIGATED':
      return {
        ...state,
        userNavigated: action.userNavigated,
      }
    case 'AUTO_HIGHLIGHT':
      if (
        state.userNavigated ||
        !state.isOpen ||
        state.activeIndex === action.index
      ) {
        return state
      }
      return {
        ...state,
        activeIndex: action.index,
        shouldScrollToActive: true,
      }
    case 'RESET_ACTIVE_INDEX':
      return {
        ...state,
        activeIndex: -1,
        userNavigated: false,
        shouldScrollToActive: false,
      }
    default:
      return state
  }
}
